import i18n from '../i18n'

/**
 * Traduz uma falha do axios num objeto que a interface sabe mostrar.
 *
 * Gêmeo de `mobile/src/services/apiError.ts`, de propósito: mesmos tipos,
 * mesmas chaves de tradução, mesmas regras. Os dois clientes falam com a mesma
 * API, e divergir aqui significaria a mesma falha explicada de dois jeitos
 * diferentes para o mesmo usuário.
 *
 * ------------------------------------------------- A REGRA QUE GOVERNA TUDO
 *
 * **A mensagem é NOSSA; a do servidor só aparece quando fala com um humano.**
 *
 * Medido contra a API em 2026-08-14, os corpos reais são:
 *
 *   401 → {"errors":["You need to sign in or sign up before continuing."]}
 *         boilerplate do devise_token_auth, em INGLÊS, que não passa pelo
 *         nosso código e nunca será traduzido lá.
 *   403 → {"errors":["Apenas o administrador do campeonato pode alterá-lo."]}
 *         português, escrito para o usuário. ESTE vale mostrar.
 *   404 → {"error":"Couldn't find EditionModality with 'id'=\"9\""}
 *         singular, inglês, e vaza nome de classe do domínio interno.
 *   422 → {"errors":[...]} — e num controller, {"errors":{campo:[...]}}
 */

/** Os dois status em que a API fala com o usuário, e não consigo mesma. */
const STATUS_COM_TEXTO_DE_DOMINIO = [403, 422]

/**
 * Rotas de autenticação, onde 401 significa OUTRA COISA.
 *
 * Senha errada no login também devolve 401. Sem esta distinção, o app leria
 * isso como sessão morta: apagaria o token, mandaria para o login — onde o
 * usuário já está — e mostraria "sua sessão expirou" para quem só errou a
 * senha.
 */
const ROTAS_DE_AUTENTICACAO = /\/auth\/(sign_in|sign_up|sign_out|password|validate_token)/

function ehTentativaDeLogin (erro) {
  return ROTAS_DE_AUTENTICACAO.test((erro && erro.config && erro.config.url) || '')
}

function t (chave) {
  // `global` é a instância do vue-i18n fora de componente. Traduzir aqui, e
  // não na hora de exibir, mantém a mensagem pronta para qualquer superfície.
  return i18n.global.t(chave)
}

export function normalizarErro (erro) {
  // ------------------------------------------------------------- sem resposta
  if (!erro || !erro.response) {
    // `ECONNABORTED` é como o axios reporta estouro de `timeout`. Distinguir de
    // "sem internet" importa: quem está sem rede precisa de outra ação do que
    // quem esperou um servidor lento.
    const expirou =
      (erro && erro.code === 'ECONNABORTED') || /timeout/i.test((erro && erro.message) || '')

    return expirou
      ? base('timeout', t('erros.demorou'), { podeTentarDeNovo: true })
      : base('offline', t('erros.sem_conexao'), { podeTentarDeNovo: true })
  }

  const status = erro.response.status
  const { detalhe, porCampo } = extrairTexto(erro.response.data, status)

  if (status === 401 && ehTentativaDeLogin(erro)) {
    return base('credenciais_invalidas', t('erros.credenciais_invalidas'), { status })
  }
  if (status === 401) {
    return base('sessao_expirada', t('erros.sessao_expirada'), {
      status,
      exigeReautenticacao: true
    })
  }
  if (status === 403) {
    // Aqui o detalhe É a mensagem: a API diz exatamente qual regra barrou, e um
    // texto genérico nosso seria pior do que o dela.
    return base('sem_permissao', detalhe || t('erros.sem_permissao'), { status, detalhe })
  }
  if (status === 404) {
    return base('nao_encontrado', t('erros.nao_encontrado'), { status })
  }
  if (status === 409) {
    return base('conflito', detalhe || t('erros.conflito'), { status, detalhe, porCampo })
  }
  if (status === 422) {
    return base('invalido', detalhe || t('erros.invalido'), { status, detalhe, porCampo })
  }
  if (status === 503) {
    return base('indisponivel', t('erros.indisponivel'), { status, podeTentarDeNovo: true })
  }
  if (status >= 500) {
    return base('servidor', t('erros.servidor'), { status, podeTentarDeNovo: true })
  }

  return base('desconhecido', t('erros.desconhecido'), { status })
}

/**
 * Lê o texto do corpo, aguentando os quatro formatos que a API emite de fato.
 * Devolve vazio para status que não falam com humano — ver o topo.
 */
function extrairTexto (corpo, status) {
  if (!STATUS_COM_TEXTO_DE_DOMINIO.includes(status)) return {}
  if (!corpo || typeof corpo === 'string') return {}

  // {"errors": ["msg", ...]} — o formato mais comum
  if (Array.isArray(corpo.errors)) {
    const textos = corpo.errors.filter(m => typeof m === 'string' && m.trim())
    return textos.length ? { detalhe: textos.join('. ') } : {}
  }

  // {"errors": {"campo": ["msg"]}} — ActiveModel::Errors serializado como hash.
  // Acontece em group_configurations, e um `errors[0]` ingênuo devolve
  // `undefined` aqui: a mensagem sumiria onde ela é mais específica.
  if (corpo.errors && typeof corpo.errors === 'object') {
    const porCampo = corpo.errors
    const textos = Object.keys(porCampo).map(
      campo => `${campo}: ${[].concat(porCampo[campo]).join(', ')}`
    )
    return textos.length ? { detalhe: textos.join('. '), porCampo } : {}
  }

  // {"error": "msg"} — SINGULAR. É o formato do rescue_from da
  // ApplicationController, e some se o normalizador só olhar `errors`.
  if (typeof corpo.error === 'string' && corpo.error.trim()) {
    return { detalhe: corpo.error }
  }

  return {}
}

function base (tipo, mensagem, extras = {}) {
  return {
    tipo,
    mensagem,
    podeTentarDeNovo: false,
    exigeReautenticacao: false,
    ...extras
  }
}

export default { normalizarErro }
